import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Document } from "components/icons/Document";
import { LinkBreak } from "components/icons/LinkBreak";
import { Padlock } from "components/icons/Padlock";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP } from "constants/invoicing";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useHistory, useLocation } from "react-router-dom";
import { useUnlinkInvoicesMutation } from "store/apis/transactions";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";

const InvoiceItem = ({
  uuid,
  invoiceNumber,
  transactionId,
  financialRecordType,
  isWrittenOff,
}: {
  uuid: string;
  invoiceNumber: string;
  transactionId: string;
  financialRecordType: string;
  isWrittenOff: boolean;
}) => {
  const { search } = useLocation();
  const history = useHistory();
  const confirmUnlinkModal = useModal();
  const hasRevenueAccess = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE,
    "READ_ONLY"
  );
  const [unlinkInvoices, { isLoading: unlinkingInvoice }] =
    useUnlinkInvoicesMutation();
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();

  const handleUnlinkInvoice = async () => {
    try {
      await unlinkInvoices({
        entityId,
        transactionId,
        financial_record_type: financialRecordType,
        groupId: group.uuid,
        invoice_id: uuid,
      }).unwrap();
      successToast({
        message: "Invoice unlinked successfully",
      });
      confirmUnlinkModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <div className="t-flex t-justify-between t-gap-2 t-h-full t-items-center">
      <div className="t-w-full t-p-1 t-border t-border-solid t-border-neutral-10 t-flex t-gap-2 t-rounded t-items-center">
        <Document color="#585170" />
        <div className="t-text-body-sm t-text-text-100">{invoiceNumber}</div>
      </div>

      <div className="t-flex t-h-full t-items-center t-gap-2">
        <Modal.Root
          open={confirmUnlinkModal.isOpen}
          onOpenChange={confirmUnlinkModal.toggle}
        >
          <LockedUi feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}>
            <ConditionalToolTip
              condition={isWrittenOff && WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP}
            >
              <span>
                <Modal.Trigger asChild>
                  <Button
                    customType="icon"
                    size="small"
                    disabled={isWrittenOff}
                    aria-label="Unlink invoice"
                  >
                    <LinkBreak />
                  </Button>
                </Modal.Trigger>
              </span>
            </ConditionalToolTip>
          </LockedUi>
          <Modal.Content size="small">
            <Modal.Body>
              <div>
                <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                  <TriangleWarning />
                </span>
                <p className="t-mb-2 t-text-body-sm t-font-medium t-mt-5">
                  Unlink invoice?
                </p>
                <div className="t-text-body-sm t-text-text-30">
                  Unlinking the invoice will change the invoice status and will
                  uncategorize the corresponding transaction.
                </div>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button
                isLoading={unlinkingInvoice}
                disabled={unlinkingInvoice}
                customType="primary"
                onClick={handleUnlinkInvoice}
              >
                Confirm
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>

        <Button
          customType="icon"
          size="xsmall"
          onClick={() =>
            history.push(`/books/invoices-and-customers/${uuid}${search}`)
          }
          disabled={!hasRevenueAccess}
        >
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.REVENUE}
            level="READ_ONLY"
            blockedUi={<Padlock />}
          >
            <SmallArrowUpRight />
          </LockedUi>
        </Button>
      </div>
    </div>
  );
};

export const LinkedInvoices = ({
  transactionId,
  invoices,
}: {
  transactionId: string;
  invoices: Invoice[];
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-2">
      {invoices.map(
        ({
          uuid,
          invoice_number,
          financial_record_type,
          written_off_amount,
        }) => (
          <InvoiceItem
            key={uuid}
            invoiceNumber={invoice_number}
            uuid={uuid}
            transactionId={transactionId}
            financialRecordType={financial_record_type}
            isWrittenOff={Boolean(written_off_amount)}
          />
        )
      )}
    </div>
  );
};
